import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Dialog,
  Drawer,
  IconButton,
  SwipeableDrawer,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExoCal from './ExoCal.jsx';
import { useEffect, useRef, useState } from 'react';
import { API_DELETE, DBRequest, useConfig } from '../../api/api';
import { ExoCalList } from './cal/cal-list/ExoCalList.jsx';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens.js';
import moment from 'moment';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ExoCalInbox } from './cal/inbox/ExoCalInbox.jsx';
import { updateOrCreate } from '../special/updateOrCreate.js';
import Cookies from 'universal-cookie';
import ExoAlert from './ExoAlert.jsx';
import ClassIcon from '@mui/icons-material/Class';
import ExoDialog from './ExoDialog.jsx';
import { ExoCalCategory } from './cal/category/ExoCalCategory.jsx';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import { IntegrationsSyncIndicator } from '../special/IntegrationsSyncIndicator.jsx';

export const ExoCalGroup = ({ apiUrl = 'calendar/own' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const config = useConfig();

  const cookies = new Cookies();

  const lastView = cookies.get('calendarLastView') || 'month';

  const [events, setEvents] = useState(null);
  const [calendars, setCalendars] = useState(null);
  const [expandCalendars, setExpandCalendars] = useState(false);
  const [startDate, setStartDate] = useState(moment());

  const [period, setPeriod] = useState(lastView);

  const calRef = useRef();

  useEffect(() => {
    if (startDate && period) {
      reqData(startDate.format('YYYY-MM-DD'));
    }
  }, [startDate, period, config, apiUrl]);

  function handleResponse(res) {
    const hiddenCals = cookies.get('hiddenCals');

    if (hiddenCals) {
      hiddenCals.forEach((id) => {
        const match = res.data.find((cal) => String(cal.id) === String(id));
        if (match) {
          match.isInactive = true;
        }
      });
    }
    setCalendars(res.data);
    generateEvents(res.data);
  }

  function generateEvents(cals) {
    var newEvents = [];

    cals.forEach((cal) => {
      if (cal.isInactive) return;
      const color = cal.color;
      cal.events?.forEach((event) => {
        newEvents.push({
          ...event,
          writePermissions: cal.writePermissions
            ? event.writePermissions
            : false,
          color,
          calendar: {
            id: cal.id,
            name: cal.name,
            isVirtual: cal.isVirtual,
          },
        });
      });
    });
    setEvents(newEvents);
  }

  function handleChange(newCalendars) {
    setCalendars(newCalendars);
    generateEvents(newCalendars);
  }

  // period change
  function addPeriod() {
    if (calRef.current?.setPopperOpen) {
      calRef.current.setPopperOpen(false);
    }
    const newStartDate = moment(startDate.format('YYYY-MM-DD')).add(
      periodMap[period].value,
      periodMap[period].key
    );
    setStartDate(newStartDate);
  }

  function subtractPeriod() {
    if (calRef.current?.setPopperOpen) {
      calRef.current.setPopperOpen(false);
    }
    const newStartDate = moment(startDate.format('YYYY-MM-DD')).subtract(
      periodMap[period].value,
      periodMap[period].key
    );
    setStartDate(newStartDate);
  }

  const periodMap = {
    day: {
      key: 'days',
      value: 1,
      add: 1,
      subtract: 0,
      format: 'ddd',
      prefix: '',
      endOf: 'day',
      startOf: 'day',
    },
    week: {
      key: 'weeks',
      value: 1,
      add: 1,
      subtract: 0,
      format: 'w',
      prefix: t('week'),
      endOf: 'week',
      startOf: 'week',
    },
    month: {
      key: 'months',
      value: 1,
      format: 'MMM',
      prefix: '',
      endOf: 'month',
      startOf: 'month',
    },
  };

  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  function reqData(startDate, periodOverride = period) {
    setLoading(true);
    const {
      key,
      add = 0,
      subtract = 0,
      endOf,
      startOf,
    } = periodMap[periodOverride];

    let momentStart = moment(startDate, 'YYYY-MM-DD').startOf(startOf);
    let momentEnd = moment(startDate, 'YYYY-MM-DD').endOf(endOf);

    // If period is "month", extend to full visible grid
    if (periodOverride === 'month') {
      momentStart = momentStart.startOf('week'); // Start of week that contains 1st day
      momentEnd = momentEnd.endOf('week'); // End of week that contains last day
    } else {
      momentStart = momentStart.subtract(subtract, key);
      momentEnd = momentEnd.add(add, key);
    }

    const reqStartDate = momentStart.format('YYYY-MM-DD');
    const reqEndDate = momentEnd.format('YYYY-MM-DD');

    DBRequest({
      config,
      path: `${apiUrl}?startDate=${reqStartDate}&endDate=${reqEndDate}`,
      onResponse: (res) => {
        handleResponse(res);
      },
      onLoading: setLoading,
    });
  }

  function handleDelete(cal, cals) {
    // Online calendar deletion is disabled - always delete locally only
    DBRequest({
      config,
      path: `calendars/${cal.id}`,
      method: API_DELETE(),
      onResponse: () => {
        handleDeletedCal(cal, cals);
      },
    });
  }

  function handleDeletedCal(cal, cals) {
    var newCalendars = cals.filter((obj) => obj.id !== cal.id);
    setCalendars(newCalendars);
    generateEvents(newCalendars);
  }

  function handleCalendarChange(res) {
    if (res.status == 2) {
      // Declining an accepted invitation already removed the user from the
      // calendar on the server, so only drop it from the local list.
      if (calendars.find((cal) => res.calendar.id == cal.id)) {
        handleDeletedCal(res.calendar, calendars);
      }
    } else {
      const newCals = updateOrCreate(calendars, res.calendar);

      handleChange(newCals);
    }
  }

  // categories
  const [openCategories, setOpenCategories] = useState(false);
  const [categories, setCategories] = useState(null);

  function handleCategoriesChange(selectedCategories) {}

  useEffect(() => {
    DBRequest({
      config,
      path: `calendar-event-categories`,
      onResponse: setCategories,
    });
  }, [config]);

  function handleChangeToggle(e, reqPeriod) {
    if (reqPeriod === period || !periodMap[reqPeriod]) return;
    setPeriod(reqPeriod);
    cookies.set('calendarLastView', reqPeriod);
    // Optionally reset startDate to start of the new period
    setStartDate((prev) => moment(prev).startOf(periodMap[reqPeriod].startOf));
  }

  //Blocked Times openBlockedTimes
  // const [openBlockedTimes, setOpenBlockedTimes] = useState(false);
  // const [blockedUserTimesData, setBlockedUserTimesData] = useState([]);

  // useEffect(() => {
  //   handleLoadUsersBlockedTimes(startDate);
  // }, [period, startDate, config]);

  // function handleLoadUsersBlockedTimes(startDate) {
  //   const dateStart = moment(startDate).startOf('isoWeek').format('YYYY-MM-DD');
  //   const dateEnd = moment(startDate).endOf('isoWeek').format('YYYY-MM-DD');
  //   console.log(dateStart, 'and ', dateEnd);

  //   console.log('About to call DBR');
  //   DBRequest({
  //     config,
  //     path: `blocked-times?startDate=${dateStart}&endDate=${dateEnd}`,
  //     onResponse: (res) => {
  //       console.log('Response received', res.data);
  //       setBlockedUserTimesData(res.data);
  //     },
  //     onLoading: setLoading,
  //   });
  // }

  return (
    <Box className="flex h-full w-full overflow-hidden">
      <Dialog
        open={expandCalendars}
        onClose={() => setExpandCalendars(false)}
        anchor="right"
        sx={{
          '.MuiPaper-root': {
            backgroundImage: 'unset!important',
          },
          '.MuiBackdrop-root': {
            backdropFilter: 'blur(1px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3 )',
          },
        }}
      >
        <ExoCalList
          calendars={calendars}
          onChange={handleChange}
          onDelete={handleDelete}
        />
      </Dialog>

      <Box className="flex flex-col h-full w-full gap-2">
        <Box className="flex gap-2 flex-wrap lg:flex-nowrap items-center">
          <ExoCalInbox
            calendars={calendars}
            onEventChange={() => reqData(startDate.format('YYYY-MM-DD'))}
            onCalendarChange={handleCalendarChange}
          />

          <Tooltip title={t('Calendars')}>
            <Button
              onClick={() => setExpandCalendars(!expandCalendars)}
              sx={{
                margin: '0px',
                padding: '0px',
                bgcolor: colors.glass,
                ':hover': {
                  bgcolor: colors.selected,
                },
                borderRadius: '8px',
                minWidth: '45px',
                height: '45px',
              }}
              className=" backdrop-blur-md h-[45px]"
            >
              <CalendarMonthIcon />
            </Button>
          </Tooltip>

          <Tooltip title={t('Categories')}>
            <Button
              onClick={() => setOpenCategories(!openCategories)}
              sx={{
                margin: '0px',
                padding: '0px',
                bgcolor: colors.glass,
                ':hover': {
                  bgcolor: colors.selected,
                },
                borderRadius: '8px',
                minWidth: '45px',
                height: '45px',
              }}
              className="backdrop-blur-md"
            >
              <ClassIcon />
            </Button>
          </Tooltip>

          <ToggleButtonGroup
            size="small"
            value={period}
            exclusive
            onChange={handleChangeToggle}
            aria-label="Time Period"
            className=" backdrop-blur-lg"
            sx={{
              borderRadius: '8px',
              bgcolor: colors.glass,
              height: '45px',
              border: `1px solid traqnsparent`,
              '.Mui-selected': {
                bgcolor: colors.info + '30',
                border: `1px solid ${colors.info}80`,
                ':hover': {
                  bgcolor: colors.info + '30',
                },
              },
              button: {
                border: `1px solid transparent`,
                width: '45px',
                borderRadius: '8px',
                ':hover': {
                  bgcolor: colors.selected,
                },
              },
            }}
          >
            {/*<ToggleButton value="day">{t('Day')}</ToggleButton>*/}
            <Tooltip title={t('Week')}>
              <ToggleButton value="week">
                <CalendarViewWeekIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t('Month')}>
              <ToggleButton value="month">
                <CalendarViewMonthIcon />
              </ToggleButton>
            </Tooltip>
            {/* <Tooltip title={t('day')}>
              <ToggleButton value="day">
                <CalendarViewMonthIcon />
              </ToggleButton>
            </Tooltip> */}
          </ToggleButtonGroup>
          {/* <Button
            onClick={() => setOpenBlockedTimes(!openBlockedTimes)}
            sx={{
              margin: '0px',
              padding: '0px',
              bgcolor: colors.glass,
              ':hover': {
                bgcolor: colors.selected,
              },
              borderRadius: '8px',
              minWidth: '45px',
              height: '45px',
            }}
            className="backdrop-blur-md"
          >
            <ViewSidebarIcon />
          </Button> */}
          <IntegrationsSyncIndicator type={'googleCalendar'} />

          <Box
            className="flex justify-between items-center px-2 py-1 rounded-lg backdrop-blur-lg z-20 w-full"
            sx={{ bgcolor: colors.glass }}
          >
            <IconButton onClick={subtractPeriod}>
              <KeyboardArrowLeftIcon />
            </IconButton>
            {/* <Typography fontWeight={600} fontSize={18}>
              {`${t(startDate.format('MMMM'))} ${startDate.format('YYYY')}`}
            </Typography> */}
            <Typography fontWeight={600} fontSize={18}>
              {period === 'day'
                ? startDate.format('MMMM D YYYY') // Day view: "August 13 2025"
                : `${t(startDate.format('MMMM'))} ${startDate.format('YYYY')}`}
            </Typography>

            <IconButton onClick={addPeriod}>
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>
        </Box>
        {!loading && calendars && (
          <ExoCal
            ref={calRef}
            events={events}
            calendars={calendars}
            onEventsChange={setEvents}
            startDate={startDate}
            selectedPeriod={period}
          />
        )}
      </Box>
      <ExoAlert
        open={Boolean(openAlert)}
        alert={openAlert || {}}
        onClose={openAlert?.onCancel}
        onSubmit={openAlert?.onSubmit}
      />
      <Dialog
        open={openCategories}
        onClose={() => setOpenCategories(false)}
        sx={{
          '.MuiPaper-root': {
            backgroundImage: 'unset!important',
          },
          '.MuiBackdrop-root': {
            backdropFilter: 'blur(1px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3 )',
          },
        }}
      >
        <ExoCalCategory
          onChange={handleCategoriesChange}
          onClose={() => setOpenCategories(false)}
          categories={categories}
          setCategories={setCategories}
        />
      </Dialog>
    </Box>
  );
};
